import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useEffect } from "react";
import { useCallback, useState } from "react";

import { useCardTypeSaveMutation } from "src/app/features/CardType/services/server/CardTypeApi";
import type { CardTypeEntity } from "src/app/features/CardType/types/CardTypeEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const CardTypeCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("card-type");

    const [formData, setFormData] = useState<CardTypeEntity>({} as CardTypeEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardTypeSaveMutation();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CARD_TYPES}`);

    // Get default value for apac card type from form definition
    useEffect(() => {
        if (formDefinition?.formConfiguration) {
            const initialValues: CardTypeEntity = {} as CardTypeEntity;

            formDefinition.formConfiguration.tabs.forEach((tab) => {
                const cardTypeField = tab.fields.find((field) => field.name === "cardType");
                if (cardTypeField?.attributes?.defaultValue !== undefined) {
                    (initialValues as Record<string, unknown>)["cardType"] = cardTypeField.attributes.defaultValue;
                }
            });

            setFormData(initialValues as CardTypeEntity);
        }
    }, [formDefinition]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "Card Type" }),
                    `${BASE_PATH}/${ResourceName.CARD_TYPES}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardTypeEntity>
                    name="CardTypeCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
